import React, { createContext, useEffect, useRef, useCallback } from "react";
import { loadRecaptchaScript } from "./loadRecaptchaScript";

// ─── Context value ───────────────────────────────────────────────────────────

export interface RecaptchaContextValue {
  /**
   * Execute a reCAPTCHA Enterprise check for the given action.
   *
   * @param action - The action name (must match what the backend expects, e.g. `"LOGIN"`).
   * @returns A promise resolving with the reCAPTCHA token string.
   * @throws If the script failed to load or execution failed.
   */
  getToken: (action: string) => Promise<string>;
}

export const RecaptchaContext = createContext<RecaptchaContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export interface RecaptchaProviderProps {
  /** The Enterprise site key. When falsy, recaptcha is disabled and `getToken` resolves with `""`. */
  siteKey?: string;
  children: React.ReactNode;
}

export const RecaptchaProvider: React.FC<RecaptchaProviderProps> = ({
  siteKey,
  children,
}) => {
  // Track whether the script has been loaded
  const scriptReadyRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!siteKey) return;
    // Kick off script loading on mount (fire-and-forget; errors handled in getToken)
    scriptReadyRef.current = loadRecaptchaScript(siteKey);
  }, [siteKey]);

  const getToken = useCallback(
    async (action: string): Promise<string> => {
      // No site key → recaptcha disabled (dev / test). Return empty token so the
      // request can still proceed; the backend's `skipIf` handles this case.
      if (!siteKey) return "";

      // Ensure the script is loaded
      if (!scriptReadyRef.current) {
        scriptReadyRef.current = loadRecaptchaScript(siteKey);
      }
      await scriptReadyRef.current;

      // Wait for the library to be ready and execute
      return new Promise<string>((resolve, reject) => {
        grecaptcha.enterprise.ready(async () => {
          try {
            const token = await grecaptcha.enterprise.execute(siteKey, { action });
            resolve(token);
          } catch (error) {
            reject(error);
          }
        });
      });
    },
    [siteKey],
  );

  return (
    <RecaptchaContext.Provider value={{ getToken }}>
      {children}
    </RecaptchaContext.Provider>
  );
};
